/**
 * MobileCutCard Component
 *
 * Card-based display for cut activity data on mobile devices.
 * Replaces the DataTable view for better touch interaction.
 */

import React from 'react';
import { Box, Typography, Skeleton } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import SmartphoneOutlinedIcon from '@mui/icons-material/SmartphoneOutlined';
import TabletOutlinedIcon from '@mui/icons-material/TabletOutlined';
import WatchOutlinedIcon from '@mui/icons-material/WatchOutlined';
import DevicesOtherOutlinedIcon from '@mui/icons-material/DevicesOtherOutlined';
import { colors, typography as typographyTokens, radii, shadows, StatusBadge } from '@components/design-system';
import { AppRoutes } from '@consts/routes';
import type { ParsedCut } from '@/types/cuts';
import { CutType } from '@api/cut';

// Cut Type color mapping for StatusBadge
const CUT_TYPE_STATUS_MAP: Record<string, { label: string; status: 'success' | 'warning' | 'info' | 'error' }> = {
    [CutType.Successful]: { label: 'Successful', status: 'success' },
    [CutType.Warranty]: { label: 'Warranty', status: 'warning' },
    [CutType.FailedCut]: { label: 'Failed', status: 'error' },
    [CutType.DiagnosticCut]: { label: 'Diagnostic', status: 'info' },
};

// Category to icon mapping
const getCategoryIcon = (category: string) => {
    const iconProps = { sx: { color: colors.primary[500], fontSize: 22 } };
    
    switch (category?.toLowerCase()) {
        case 'smartphones':
            return <SmartphoneOutlinedIcon {...iconProps} />;
        case 'tablets':
            return <TabletOutlinedIcon {...iconProps} />;
        case 'smartwatches':
            return <WatchOutlinedIcon {...iconProps} />;
        default:
            return <DevicesOtherOutlinedIcon {...iconProps} />;
    }
};

interface MobileCutCardProps {
    data: ParsedCut;
}

const InfoRow: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 0.5 }}>
        <Typography
            sx={{
                fontSize: typographyTokens.fontSize.xs,
                color: colors.neutral[500],
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
            }}>
            {label}
        </Typography>
        <Typography
            sx={{
                fontSize: typographyTokens.fontSize.sm,
                color: colors.neutral[700],
                fontWeight: typographyTokens.fontWeight.medium,
                textAlign: 'right',
                maxWidth: '60%',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
            }}>
            {value || '-'}
        </Typography>
    </Box>
);

export const MobileCutCard: React.FC<MobileCutCardProps> = ({ data }) => {
    const cutTypeConfig = CUT_TYPE_STATUS_MAP[data.cutType];

    return (
        <Box
            component={RouterLink}
            to={`${AppRoutes.CutHistory}?id=${data.dbCutId}`}
            sx={{
                display: 'block',
                textDecoration: 'none',
                backgroundColor: colors.neutral[0],
                borderRadius: `${radii.lg}px`,
                border: `1px solid ${colors.neutral[200]}`,
                p: 2,
                mb: 1.5,
                boxShadow: shadows.card,
                transition: 'all 150ms ease',
                '&:hover': {
                    boxShadow: shadows.elevated,
                    borderColor: colors.primary[200],
                },
                '&:active': {
                    transform: 'scale(0.99)',
                },
            }}>
            {/* Header with icon and cut info */}
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, mb: 2 }}>
                <Box
                    sx={{
                        width: 40,
                        height: 40,
                        borderRadius: `${radii.md}px`,
                        backgroundColor: `${colors.primary[500]}15`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                    }}>
                    {getCategoryIcon(data.category)}
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                        <Typography
                            sx={{
                                fontSize: typographyTokens.fontSize.md,
                                fontWeight: typographyTokens.fontWeight.semibold,
                                color: colors.neutral[900],
                                lineHeight: 1.3,
                            }}>
                            {data.manufacturer} {data.model}
                        </Typography>
                        {cutTypeConfig && <StatusBadge label={cutTypeConfig.label} status={cutTypeConfig.status} size='small' />}
                    </Box>
                    <Typography
                        sx={{
                            fontSize: typographyTokens.fontSize.xs,
                            color: colors.neutral[500],
                            mt: 0.25,
                        }}>
                        Cut ID: {data.cutId}
                    </Typography>
                </Box>
            </Box>

            {/* Details */}
            <Box sx={{ borderTop: `1px solid ${colors.neutral[100]}`, pt: 1 }}>
                <InfoRow label='Cut Date' value={data.cutDate} />
                <InfoRow label='Category' value={data.category} />
                <InfoRow label='Material' value={data.material} />
                <InfoRow label='Location' value={data.location} />
                <InfoRow label='Company' value={data.company} />
                {data.serialNum && <InfoRow label='Serial #' value={data.serialNum} />}
            </Box>
        </Box>
    );
};

// Loading skeleton for mobile cards
export const MobileCutCardSkeleton: React.FC = () => (
    <Box
        sx={{
            backgroundColor: colors.neutral[0],
            borderRadius: `${radii.lg}px`,
            border: `1px solid ${colors.neutral[200]}`,
            p: 2,
            mb: 1.5,
        }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, mb: 2 }}>
            <Skeleton variant='rounded' width={40} height={40} />
            <Box sx={{ flex: 1 }}>
                <Skeleton variant='text' width='80%' height={24} />
                <Skeleton variant='text' width='50%' height={16} />
            </Box>
        </Box>
        <Box sx={{ borderTop: `1px solid ${colors.neutral[100]}`, pt: 1 }}>
            <Skeleton variant='text' width='100%' height={20} />
            <Skeleton variant='text' width='100%' height={20} />
            <Skeleton variant='text' width='100%' height={20} />
            <Skeleton variant='text' width='100%' height={20} />
        </Box>
    </Box>
);

export default MobileCutCard;
